import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document } from '@contentful/rich-text-types';
import type * as Stitches from '@stitches/react';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';
import { config } from '@zoopla-eng/zds-components/themes';
import { CmsImage } from '@zoopla/cms/image';
import ImagePlaceholder from '../assets/image-placeholder.svg';
import { BannerTitle, BannerContainer } from './banner.styles';
import { bannerContentClass, imageClass } from './banner.vanilla.styles.css';
import { BANNER_RENDER_OPTIONS } from './render-options';

type CSS = Stitches.CSS<typeof config>;

type AllowedProps = Pick<
    CSS,
    'p' | 'pt' | 'pr' | 'pb' | 'pl' | 'width' | 'gridColumn'
>;

type BannerButton = {
    label: string;
    url: string;
    category: 'primary' | 'secondary' | 'tertiary';
};

export type BannerProps = {
    /**
     * defaults to `bottom`
     */
    imagePosition?: 'top' | 'bottom';
    imageSrc: string;
    imageAlt?: string;

    /**
     * defaults to `false`. `gridColumn` will overwrite.
     */
    fw?: boolean;

    title: string;
    body: Document;

    /**
     * defaults to `brandPrimaryT4` which is `purple`
     */
    bgColor?: 'purple' | 'white' | 'green' | 'blue' | 'maroon' | 'orange';

    buttons: BannerButton[];
} & AllowedProps;

export const Banner = ({
    imagePosition = 'bottom',
    fw = false,
    imageSrc,
    imageAlt = '',
    title,
    body,
    bgColor,
    buttons,
    ...css
}: BannerProps) => (
    <Box fw={fw} {...css}>
        <BannerContainer bgColor={bgColor} imagePosition={imagePosition}>
            <div className={bannerContentClass}>
                <BannerTitle>{title}</BannerTitle>
                <Box pt="$S200" pb="$S200">
                    {documentToReactComponents(body, BANNER_RENDER_OPTIONS)}
                </Box>
                <Box display="flex" flexWrap="wrap">
                    {buttons.map(({ category, label, url }) => (
                        <Button
                            key={label}
                            category={category}
                            {...{ as: 'a', href: url }}
                        >
                            {label}
                        </Button>
                    ))}
                </Box>
            </div>

            <CmsImage
                imgClassName={imageClass}
                pictureClassName={imageClass}
                src={imageSrc}
                alt={imageAlt}
                onError={(e) => (e.currentTarget.src = ImagePlaceholder)}
            />
        </BannerContainer>
    </Box>
);
